import React, { useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Context Providers
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';

// Custom Hooks
import { useAuth } from './hooks/useAuth';
import { useTheme } from './hooks/useTheme';

// Components
import Login from './components/Login';
import Header from './components/Header';
import Navigation from './components/Navigation';
import Dashboard from './components/Dashboard';
import AddWorkout from './components/AddWorkout';
import History from './components/History';
import Leaderboard from './components/Leaderboard';
import Rewards from './components/Rewards';
import Workouts from './components/Workouts';
import Admin from './components/Admin';

// Main App Component
const App = () => {
  const { user, loading } = useAuth();
  const { darkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [refreshKey, setRefreshKey] = useState(0);

  const handleWorkoutAdded = () => {
    // Refresh dashboard data
    setRefreshKey(prev => prev + 1);
    // Switch to dashboard to see the update
    setActiveTab('dashboard');
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard key={refreshKey} />;
      case 'add-workout':
        return <AddWorkout onWorkoutAdded={handleWorkoutAdded} />;
      case 'workouts':
        return <Workouts />;
      case 'history':
        return <History />;
      case 'leaderboard':
        return <Leaderboard />;
      case 'rewards':
        return <Rewards />;
      case 'admin':
        return <Admin />;
      default:
        return <Dashboard key={refreshKey} />;
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <Header />
      <Navigation activeTab={activeTab} setActiveTab={setActiveTab} />
      <main>
        {renderContent()}
      </main>
    </div>
  );
};

// Export the app wrapped with providers
export default function WorkoutTracker() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <App />
        {/* Custom styles for confirmation toast and dark theme */}
        <style jsx global>{`
          .custom-confirm-toast {
            background: white !important;
            color: black !important;
            border: 1px solid #e5e7eb !important;
            border-radius: 12px !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
            min-width: 350px !important;
            max-width: 400px !important;
          }
          
          .custom-confirm-toast .Toastify__toast-body {
            padding: 0 !important;
            margin: 0 !important;
          }

          /* Dark theme styles for confirmation toast */
          html.dark .custom-confirm-toast {
            background: #374151 !important;
            color: white !important;
            border: 1px solid #4b5563 !important;
          }
          
          /* Dark theme styles for regular toasts */
          html.dark .Toastify__toast-container {
            background: transparent !important;
          }
          
          html.dark .Toastify__toast {
            background: #374151 !important;
            color: white !important;
            border: 1px solid #4b5563 !important;
          }
          
          html.dark .Toastify__toast--success {
            background: #059669 !important;
            color: white !important;
          }
          
          html.dark .Toastify__toast--error {
            background: #dc2626 !important;
            color: white !important;
          }
          
          html.dark .Toastify__toast--warning {
            background: #d97706 !important;
            color: white !important;
          }
          
          html.dark .Toastify__toast--info {
            background: #2563eb !important;
            color: white !important;
          }
          
          html.dark .Toastify__progress-bar {
            background: #3b82f6 !important;
          }
          
          html.dark .Toastify__close-button {
            color: white !important;
            opacity: 0.7 !important;
          }
          
          html.dark .Toastify__close-button:hover {
            opacity: 1 !important;
          }
        `}</style>
        {/* ToastContainer with dynamic theme */}
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
      </AuthProvider>
    </ThemeProvider>
  );
}